import { get, onValue, ref } from 'firebase/database';
import { noop } from 'lodash';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { database } from '@skippay/core/firebase/firebase';
import {
	BreadcrumbCategory,
	captureBreadcrumb,
	captureMessage,
} from '@skippay/core/global/errorTracking';
import {
	getLatestUpdateTimestamp,
	getUserFirebaseUid,
	setBanners,
	setConfig,
	setModals,
	setRequests,
} from '@skippay/core/modules/user';

function getDatabaseRef(user: string, collection: string) {
	return ref(database, `users/${user}/${collection}`);
}

export function useFirebaseSync(): void {
	const dispatch = useDispatch();
	const userUid = useSelector(getUserFirebaseUid);
	const lastUpdateTimestamp = useSelector(getLatestUpdateTimestamp);

	useEffect(() => {
		if (!userUid) {
			return () => noop;
		}

		const bannersListener = onValue(
			getDatabaseRef(userUid, 'banners'),

			(snapshot) => {
				const banners = snapshot.val();
				dispatch(setBanners(banners));
			},
			noop
		);

		const modalsListener = onValue(
			getDatabaseRef(userUid, 'modals'),
			(snapshot) => {
				const modals = snapshot.val();
				dispatch(setModals(modals));
			},
			noop
		);

		const requestsListener = onValue(
			getDatabaseRef(userUid, 'requests'),
			(snapshot) => {
				const requests = snapshot.val();
				dispatch(setRequests(requests));
			},
			noop
		);

		const configListener = onValue(
			getDatabaseRef(userUid, 'config'),
			(snapshot) => {
				const config = snapshot.val();
				dispatch(setConfig(config));
			},
			noop
		);

		return () => {
			bannersListener();
			modalsListener();
			requestsListener();
			configListener();
		};
	}, [dispatch, userUid]);

	useEffect(() => {
		if (!userUid) {
			return;
		}

		// Check only on mount and only every 20 minutes
		if (lastUpdateTimestamp && lastUpdateTimestamp < Date.now() - 20 * 60 * 1000) {
			return;
		}

		captureBreadcrumb(BreadcrumbCategory.firebaseDatabase, 'Downloading latest data from Firebase');

		try {
			const bannersPromise = get(getDatabaseRef(userUid, 'banners'));
			const modalsPromise = get(getDatabaseRef(userUid, 'modals'));
			const requestsPromise = get(getDatabaseRef(userUid, 'requests'));
			const configPromise = get(getDatabaseRef(userUid, 'config'));

			Promise.all([bannersPromise, modalsPromise, requestsPromise, configPromise]).then(
				([bannersSnapshot, modalsSnapshot, requestsSnapshot, configSnapshot]) => {
					if (bannersSnapshot.exists()) {
						const banners = bannersSnapshot.val();
						dispatch(setBanners(banners));
					} else {
						dispatch(setBanners({}));
					}

					if (modalsSnapshot.exists()) {
						const modals = modalsSnapshot.val();
						dispatch(setModals(modals));
					} else {
						dispatch(setModals({}));
					}

					if (requestsSnapshot.exists()) {
						const requests = requestsSnapshot.val();
						dispatch(setRequests(requests));
					} else {
						dispatch(setRequests({}));
					}

					if (configSnapshot.exists()) {
						const config = configSnapshot.val();
						dispatch(setConfig(config));
					}
				}
			);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			captureMessage('Error while downloading latest data from Firebase', error);
		}
	}, [dispatch, userUid]);
}
